import { ModalNewResourceContextProvider as ModalNewResourceContextProviderBusiness } from '@fabric-app/business/resources/components/NewResource/ModalNewResourceContext';
import type React from 'react';
import useUIStore, { type NewModalOptions } from '../store/ui';
import { pick } from '@fabric-app/utils/object/manipulation';
import { shallow } from 'zustand/shallow';
import type { PropsWithChildren } from 'react';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useMutationCreateResourceV2 } from '@fabric-app/business/resources/mutations/useMutationCreateResourceV2';
import { useDestinationParentFolderFromPathname } from '@fabric-app/business/resources/components/NewResource/useDestinationParentFolderFromPathname';
import { getYdocBinaryStateFromHTML } from '@fabric-app/business/Tiptap/utils/getEmptyYdocBinaryState';
import { useDefaultResourceClickHandler } from '@fabric-app/compat';
import { NewResourceType } from '@fabric-app/business/resources/components/NewResource/config';
import { useFeatureGuard_createResources } from '@fabric-app/business/pricing/hooks/useFeatureGuard';
export const ModalNewResourceContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { newModalOptions, setNewModalOptions, isNewModalOpen, setNewModalOpen } = useUIStore(
    (s) => pick(s, ['newModalOptions', 'setNewModalOptions', 'isNewModalOpen', 'setNewModalOpen']),
    shallow,
  );

  const { isMobileView } = useResponsive();

  const { mutate, isPending } = useMutationCreateResourceV2();
  const { defaultClickResource } = useDefaultResourceClickHandler();

  const parent = useDestinationParentFolderFromPathname();
  const featureGuardCreateResource = useFeatureGuard_createResources();

  const overrideSetNewModalOpen = (open: boolean, newModalOptions?: NewModalOptions) => {
    if (isPending) return;

    const openModal = () => {
      if (newModalOptions?.type === NewResourceType.Note && !isMobileView) {
        setNewModalOpen(false);
        mutate(
          {
            disableToast: true,
            data: {
              createType: 'note',
              body: {
                name: null,
                parentId: parent.id,
                ydoc: Array.from(
                  getYdocBinaryStateFromHTML(
                    newModalOptions.dataTransfer?.items?.['text/html'] ||
                      newModalOptions.dataTransfer?.items?.['text/plain'] ||
                      newModalOptions.text,
                  ),
                ),
                tags: [],
              },
              optimisticData: {
                tags: [],
                parent: parent,
                preview: {
                  content: '',
                },
              },
            },
          },
          {
            onSuccess: (data) => {
              defaultClickResource(data);
            },
          },
        );
      } else {
        setNewModalOpen(open, newModalOptions);
      }
    };

    if (newModalOptions?.type) {
      featureGuardCreateResource.check([newModalOptions.type], {
        onAllowed: openModal,
        showUpsellBannerOnNotAllowed: true,
      });
    } else {
      openModal();
    }
  };

  return (
    <ModalNewResourceContextProviderBusiness
      value={{
        newModalOptions,
        setNewModalOptions,
        isNewModalOpen,
        setNewModalOpen: overrideSetNewModalOpen,
      }}
    >
      {children}
    </ModalNewResourceContextProviderBusiness>
  );
};
